import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect } from 'react';
import toast from 'react-hot-toast';
import { aiCoachService, type CoachInsightView } from '../services/aiCoach.service';

export const aiCoachKeys = {
  all: ['ai-coach'] as const,
  coach: (okrId: string) => [...aiCoachKeys.all, 'coach', okrId] as const,
};

/**
 * Load the cached AI Coach for an OKR. If the response status is 'generating' or 'stale',
 * poll every 4 s until it settles to 'ready' or 'failed'.
 */
export const useAICoach = (okrId: string | undefined, opts?: { enabled?: boolean }) => {
  const enabled = (opts?.enabled ?? true) && !!okrId;

  const q = useQuery<CoachInsightView, Error>({
    queryKey: okrId ? aiCoachKeys.coach(okrId) : ['ai-coach', 'noop'],
    queryFn: () => aiCoachService.getCoach(okrId!),
    enabled,
    staleTime: 30_000,
    retry: (failureCount, err) => {
      const e = err as unknown as { response?: { status?: number; data?: { code?: string } } };
      // Don't retry the "not configured" empty state
      if (e?.response?.data?.code === 'AI_NOT_CONFIGURED') return false;
      if (e?.response?.status === 401 || e?.response?.status === 403) return false;
      return failureCount < 2;
    },
    refetchInterval: (query) => {
      const data = query.state.data as CoachInsightView | undefined;
      if (!data) return false;
      if (data.isRefreshing || data.status === 'generating') return 4000;
      return false;
    },
  });

  // When cache becomes 'stale', schedule one refetch (the GET will kick a regen).
  useEffect(() => {
    if (q.data?.status === 'stale' && okrId) {
      const t = setTimeout(() => q.refetch(), 250);
      return () => clearTimeout(t);
    }
  }, [q.data?.status, okrId, q]);

  return q;
};

export const useRefreshAICoach = (okrId: string | undefined) => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () => {
      if (!okrId) throw new Error('okrId is required');
      return aiCoachService.getCoach(okrId, { force: true });
    },
    onSuccess: (data) => {
      if (okrId) qc.setQueryData(aiCoachKeys.coach(okrId), data);
    },
    onError: (err: Error) => toast.error(err.message || 'Failed to refresh AI Coach'),
  });
};
